import { type PermissionRequest, RequestSchema } from '@gestion-permisos/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/api-context.js';
import type { ApiClient } from '../api/client.js';
import { saveBlob } from '../analytics/save-file.js';

const documentsPath = (requestId: string) => `/api/v1/requests/${encodeURIComponent(requestId)}/documents`;

/** Tras adjuntar o retirar: la solicitud devuelta pasa a la caché y se vuelve a pedir su historial. */
function useAfterDocumentChange() {
  const queryClient = useQueryClient();
  return (request: PermissionRequest) => {
    queryClient.setQueryData(['requests', 'detail', request.id], request);
    void queryClient.invalidateQueries({ queryKey: ['requests', 'history', request.id] });
  };
}

/**
 * `POST /requests/{id}/documents` (09 §6.11): el archivo como cuerpo binario, con su tipo en `Content-Type`
 * y el nombre en `X-File-Name`. Sin `Idempotency-Key` ni `If-Match`: un segundo intento recibe 409
 * `DOCUMENT_EXISTS`. La API comprueba el tipo, la firma del contenido y el tamaño. La comparten el detalle
 * (`useAttachDocument`) y el alta, que adjunta el archivo elegido en el formulario tras el 201.
 */
export const postDocument = (api: ApiClient, requestId: string, file: File) =>
  api.mutate('POST', documentsPath(requestId), file, { schema: RequestSchema, headers: { 'X-File-Name': encodeURIComponent(file.name) } });

/** Adjuntar en el detalle de un borrador ya creado. */
export function useAttachDocument(requestId: string) {
  const api = useApi();
  const afterChange = useAfterDocumentChange();
  return useMutation({
    mutationFn: (file: File) => postDocument(api, requestId, file),
    onSuccess: afterChange,
  });
}

/** `DELETE /requests/{id}/documents/{documentId}`: retira el documento del borrador. */
export function useRemoveDocument(requestId: string) {
  const api = useApi();
  const afterChange = useAfterDocumentChange();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.mutate('DELETE', `${documentsPath(requestId)}/${encodeURIComponent(documentId)}`, undefined, { schema: RequestSchema }),
    onSuccess: afterChange,
  });
}

/**
 * Descarga por la API (`GET …/content`): nunca una URL del almacén de documentos. El archivo se guarda con
 * el nombre de `Content-Disposition` o, si no llega, con el del documento.
 */
export function useDownloadDocument(requestId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: async (document: { id: string; fileName: string }) => {
      const file = await api.download(`${documentsPath(requestId)}/${encodeURIComponent(document.id)}/content`);
      saveBlob(file.blob, file.fileName ?? document.fileName);
    },
  });
}
